/**
 * Touch drivers — where touch time goes without the claim moving (Shayna, 2026-10-18:
 * "add this page after Touch profile"). The approved prototype is
 * touch-drivers-handoff.zip; the definitions are MEASURE-RULES §5.8 and
 * data/touchDrivers.ts.
 *
 * Built from Touch detail's rows: the touches people MADE, by touch date, for the top
 * bar's months and people. Every count here is rows of Touch detail, so it does not tie
 * to the Touch profile, which counts touches on resolved claims (Rule 5.4).
 *
 * Clicking a row holds the page to it (status, action, step, person, department); again
 * releases it. The Statuses table ignores the held status and the Actions table the held
 * action, so either can be switched without clearing first. The training bullets read
 * only the top bar's filters.
 */
import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useAppData } from '@/data/AppData'
import { useLoadedDataset } from '@/data/DatasetProvider'
import { useFilters } from '@/data/filters'
import { useTouchDetail } from '@/data/touchDetail'
import { useTouchPeople, touchColumnPicks } from '@/data/touchScope'
import { driverSource, previousMonth, type DriverSource } from '@/data/touchDriverRows'
import {
  THIN_NOTES,
  computeDrivers,
  perClaimBand,
  repeatBand,
  repeatRate,
  restatesStatus,
  tallyAll,
  tallyBy,
  touchesPerClaim,
  shareOfTouches,
  type Drivers,
  type Loop,
  type Stuck,
  type Tally,
} from '@/data/touchDrivers'
import { useOpenTouchDetail, type TouchDriversDrill } from '@/data/touchDrill'
import { useOpenClaimDetail } from '@/data/claimDrill'
import { DataTable, type Column } from '@/components/Table'
import { ExportBar } from '@/components/ExportBar'
import { PopOut } from '@/components/PopOut'
import { PersonCell, Stat } from '@/components/Bits'
import { StackColumns } from '@/components/StackColumns'
import { dec, int, monthSpan, monthYear, pct } from '@/format'
import { useUrlState, maybeText, text } from '@/ui/urlState'
import type { ExportColumn } from '@/ui/export'

/** Rows a table shows; enlarged, up to a thousand. */
const SHOWN = 150
const SHOWN_ENLARGED = 1000
const TABLE_HEIGHT = 440

/** Per-viewer conveniences only. */
const read = (k: string) => {
  try {
    return localStorage.getItem(k)
  } catch {
    return null
  }
}
const write = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v)
  } catch {
    /* private window: not remembered */
  }
}

type Band = 'good' | 'warn' | 'crit' | null
const Chip = ({ band, children }: { band: Band; children: ReactNode }) =>
  band ? <span className={`pts-chip ${band}`}>{children}</span> : <span className="mono">{children}</span>

const mono = (s: ReactNode) => (
  <span className="mono" style={{ fontSize: 'var(--t-table)' }}>
    {s}
  </span>
)

/** A number that opens its rows elsewhere. */
function Link({ children, onClick, title }: { children: ReactNode; onClick: () => void; title: string }) {
  return (
    <button
      type="button"
      className="mono td-link"
      title={title}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      {children}
    </button>
  )
}

/** The repeat count with its bar, against the table's largest. */
function RepeatCell({ n, max, link }: { n: number; max: number; link: ReactNode }) {
  return (
    <span className="minibar" style={{ justifyContent: 'flex-end' }}>
      <span className="track">
        <span className="fill" style={{ width: `${max ? (100 * n) / max : 0}%`, background: 'var(--serious)' }} />
      </span>
      {link}
    </span>
  )
}

/** A text section with Collapse / Expand, remembered per viewer. */
function Fold({
  id,
  title,
  sub,
  startOpen,
  children,
}: {
  id: string
  title: string
  sub?: ReactNode
  startOpen: boolean
  children: ReactNode
}) {
  const key = `cpc.td.${id}`
  const [open, setOpen] = useState(() => {
    const v = read(key)
    return v === null ? startOpen : v === '1'
  })
  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <h2 className="card-heading" style={{ flex: 1 }}>
          {title}
        </h2>
        <button
          type="button"
          className="enlarge-btn"
          aria-expanded={open}
          onClick={() => {
            write(key, open ? '0' : '1')
            setOpen(!open)
          }}
        >
          {open ? 'Collapse' : 'Expand'}
        </button>
      </div>
      {open && (
        <>
          {sub && (
            <p className="body-note" style={{ margin: '4px 0 0', maxWidth: 'none' }}>
              {sub}
            </p>
          )}
          <div style={{ marginTop: 10 }}>{children}</div>
        </>
      )}
    </section>
  )
}

/** A table section: card heading, note, search, Enlarge · Export Copy · CSV, the table. */
function TableCard<T>({
  title,
  note,
  search,
  count,
  filename,
  exportColumns,
  rows,
  columns,
  rowKey,
  selectedKey,
  onRowClick,
  initialSort,
  sortParam,
  storageKey,
  empty,
  head,
}: {
  title: string
  note: ReactNode
  search?: { value: string; set: (v: string) => void; placeholder: string }
  count?: string
  filename: string
  exportColumns: ExportColumn<T>[]
  rows: T[]
  columns: Column<T>[]
  rowKey: (r: T) => string
  selectedKey: string | null
  onRowClick: (r: T) => void
  initialSort: string
  sortParam?: string
  /** Names the table for the column widths a viewer sets (DataTable). */
  storageKey: string
  empty: string
  head?: ReactNode
}) {
  return (
    <PopOut title={title}>
      {({ button, enlarged }) => (
        <div className="card popout-fill" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {!enlarged && <h2 className="card-heading">{title}</h2>}
          <p className="body-note" style={{ margin: '4px 0 0', maxWidth: 'none', fontSize: 'var(--t-micro)' }}>
            {note}
          </p>
          {head}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {search && (
              <input
                type="search"
                value={search.value}
                placeholder={search.placeholder}
                aria-label={search.placeholder}
                onChange={(e) => search.set(e.target.value)}
                className="td-search"
              />
            )}
            {count && <span style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>{count}</span>}
            <div style={{ marginLeft: 'auto' }}>
              <ExportBar lead={button} filename={filename} rows={rows} columns={exportColumns} />
            </div>
          </div>
          <DataTable
            storageKey={storageKey}
            rows={rows}
            columns={columns}
            rowKey={rowKey}
            selectedKey={selectedKey}
            onRowClick={onRowClick}
            initialSort={initialSort}
            initialDir="desc"
            sortParam={sortParam}
            maxRows={enlarged ? SHOWN_ENLARGED : SHOWN}
            maxHeight={enlarged ? undefined : TABLE_HEIGHT}
            empty={empty}
          />
        </div>
      )}
    </PopOut>
  )
}

/** What the page is held to by row clicks. */
interface Held {
  status: string | null
  action: string | null
  person: string | null
  dept: string | null
}

/** The page's tabs (Yehuda, 2026-10-21, from the reworked mockup). */
const TABS = [
  { key: 'overview', label: 'Overview' },
  // Shayna, review #887: "By status, action vs steps, by employee vs people".
  { key: 'steps', label: 'By status / action' },
  { key: 'people', label: 'By employee' },
  { key: 'moving', label: 'Not moving' },
] as const
type TabKey = (typeof TABS)[number]['key']

/**
 * Who is in the top bar's filters, each person's department and title, the held ids and
 * the filters built on them — per computation, since a source numbers its own people,
 * statuses and actions.
 */
function selectionOf(
  c: { src: DriverSource; drv: Drivers } | null,
  held: Held,
  inFilter: ReadonlySet<string>,
  byName: ReadonlyMap<string, { department: string; jobTitle: string }>,
) {
  if (!c) return null
  const { people, statuses, actions, rows: r } = c.src
  const who = {
    inFilter: people.map((n) => inFilter.has(n)),
    dept: people.map((n) => byName.get(n)?.department ?? ''),
    title: people.map((n) => byName.get(n)?.jobTitle ?? ''),
  }
  const ids = {
    status: held.status === null ? null : statuses.indexOf(held.status),
    action: held.action === null ? null : actions.indexOf(held.action),
    person: held.person === null ? null : people.indexOf(held.person),
  }
  /** A row filter, with any of the holds left off. */
  const keepOf = (skip: Partial<Record<keyof Held, true>> = {}) => {
    const s = skip.status ? null : ids.status
    const a = skip.action ? null : ids.action
    const p = skip.person ? null : ids.person
    const d = skip.dept ? null : held.dept
    return (i: number) => {
      const e = r.person[i]!
      if (!who.inFilter[e]) return false
      if (d !== null && who.dept[e] !== d) return false
      if (s !== null && r.status[i] !== s) return false
      if (a !== null && r.action[i] !== a) return false
      if (p !== null && e !== p) return false
      return true
    }
  }
  const loopsFor = (skip: Partial<Record<keyof Held, true>> = {}): Loop[] =>
    c.drv.loops.filter(
      (l) =>
        who.inFilter[l.person] &&
        (skip.dept || held.dept === null || who.dept[l.person] === held.dept) &&
        (skip.status || ids.status === null || l.status === ids.status) &&
        (skip.action || ids.action === null || l.action === ids.action) &&
        (skip.person || ids.person === null || l.person === ids.person),
    )
  const stuckFor = (): Stuck[] =>
    c.drv.stuck.filter(
      (s) =>
        who.inFilter[s.owner] &&
        (held.dept === null || who.dept[s.owner] === held.dept) &&
        (ids.status === null || s.status === ids.status) &&
        (ids.person === null || s.owner === ids.person),
    )
  return { who, ids, keepOf, loopsFor, stuckFor }
}

export function TouchDrivers() {
  const { scope, derived, openMonths } = useAppData()
  const { filters } = useFilters()
  const dataset = useLoadedDataset()
  const { data, error } = useTouchDetail()
  const openTouches = useOpenTouchDetail()
  const openClaims = useOpenClaimDetail()

  // Held by clicks. Status, action, department, month and tab name nobody: in the URL.
  // A person stays in the history entry (ui/urlState.ts).
  const [hs, setHs] = useUrlState('st', null, maybeText)
  const [ha, setHa] = useUrlState('ac', null, maybeText)
  const [hd, setHd] = useUrlState('dept', null, maybeText)
  const [hp, setHp] = useUrlState('who', null, maybeText, { personal: true })
  const [hmRaw, setHm] = useUrlState('month', null, maybeText)
  const [tabRaw, setTab] = useUrlState('tab', '', text)
  const tab: TabKey = TABS.some((t) => t.key === tabRaw) ? (tabRaw as TabKey) : 'overview'
  const months = scope.months
  // A held month the top bar's range has since left out is not held any more.
  const hm = hmRaw !== null && months.includes(hmRaw) ? hmRaw : null
  const held: Held = { status: hs, action: ha, person: hp, dept: hd }
  const [view, setView] = useState<'loops' | 'stuck'>(() => (read('cpc.td.view') === 'stuck' ? 'stuck' : 'loops'))
  const [stepQ, setStepQ] = useState('')
  const [personQ, setPersonQ] = useState('')

  /*
   * Whose touches. The figures are computed over everyone the top bar allows but its
   * Department and Job title, then held to those: a stuck claim is judged on every
   * touch it had, whoever made them, and counted under the department of whoever
   * touched it most (the prototype's reading).
   */
  const inFilter = useTouchPeople()
  const base = useTouchPeople({ department: [], jobTitle: [] })
  const picks = useMemo(() => touchColumnPicks(data, filters), [data, filters])
  const inFilterNames = useMemo(() => new Set(inFilter.map((p) => p.employee)), [inFilter])
  const baseNames = useMemo(() => new Set(base.map((p) => p.employee)), [base])

  // The month before a range, to tell whether its first month's touches repeat. Only a
  // month the file holds every made touch for.
  const fileMonths = useMemo(() => new Set(dataset.touchMonths.map((r) => r.month)), [dataset.touchMonths])
  const lookBackOf = (first: string | undefined): string[] => {
    if (!first) return []
    const prev = previousMonth(first)
    return fileMonths.has(prev) ? [prev] : []
  }
  const lookBack = useMemo(() => lookBackOf(months[0]), [months, fileMonths]) // eslint-disable-line react-hooks/exhaustive-deps
  /** The top bar's range: the month chart and the training cards. */
  const full = useMemo(() => {
    if (!data) return null
    const src = driverSource(data, { months, lookBack, people: baseNames, picks })
    return { src, drv: computeDrivers(src.rows) }
  }, [data, months, lookBack, baseNames, picks])
  /**
   * A held month is that month as its own range, the way the top bar would pick it:
   * repeats look back to the month before, loops and stuck claims are that month's.
   */
  const heldLookBack = useMemo(() => (hm ? lookBackOf(hm) : lookBack), [hm, lookBack, fileMonths]) // eslint-disable-line react-hooks/exhaustive-deps
  const computed = useMemo(() => {
    if (!hm) return full
    if (!data) return null
    const src = driverSource(data, { months: [hm], lookBack: heldLookBack, people: baseNames, picks })
    return { src, drv: computeDrivers(src.rows) }
  }, [hm, full, data, heldLookBack, baseNames, picks])
  const fullSel = useMemo(
    () => selectionOf(full, held, inFilterNames, derived.byName),
    [full, hs, ha, hp, hd, inFilterNames, derived], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const sel = useMemo(
    () => selectionOf(computed, held, inFilterNames, derived.byName),
    [computed, hs, ha, hp, hd, inFilterNames, derived], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const who = sel?.who ?? null
  const ids = sel?.ids ?? null

  const view$ = useMemo(() => {
    if (!computed || !sel) return null
    const { src, drv } = computed
    const r = src.rows
    const keep = sel.keepOf()
    const A = src.actions.length
    return {
      kpi: tallyAll(r, drv, keep),
      byStep: tallyBy(r, drv, keep, (i) => r.status[i]! * A + r.action[i]!),
      byStepAll: tallyBy(r, drv, sel.keepOf({ status: true, action: true }), (i) => r.status[i]! * A + r.action[i]!),
      byPerson: tallyBy(r, drv, sel.keepOf({ person: true }), (i) => r.person[i]!),
      loops: sel.loopsFor(),
      stuck: sel.stuckFor(),
      A,
    }
  }, [computed, sel])

  /** Every month of the range, whatever month is held. */
  const byMonth = useMemo(() => {
    if (!full || !fullSel) return null
    const r = full.src.rows
    const t = tallyBy(r, full.drv, fullSel.keepOf(), (i) => r.month[i]!)
    return months.map((m) => [m, t.get(full.src.months.indexOf(m))] as const)
  }, [full, fullSel, months])

  /** Where to focus training: the top bar's filters and range only, no holds. */
  const training = useMemo(() => {
    if (!full || !fullSel) return null
    const { src, drv } = full
    const r = src.rows
    const keep = (i: number) => fullSel.who.inFilter[r.person[i]!]!
    const A = src.actions.length
    const steps = [...tallyBy(r, drv, keep, (i) => r.status[i]! * A + r.action[i]!)]
      .filter(([, t]) => t.repeats > 0)
      .sort(([, a], [, b]) => b.repeats - a.repeats || b.touches - a.touches)
    const people = [...tallyBy(r, drv, keep, (i) => r.person[i]!)]
    const stuck = drv.stuck.filter((s) => fullSel.who.inFilter[s.owner])
    const kpi = tallyAll(r, drv, keep)
    return { steps, people, stuck, kpi, A }
  }, [full, fullSel])

  const hold = (next: Partial<Held>) => {
    if ('status' in next) setHs(next.status ?? null)
    if ('action' in next) setHa(next.action ?? null)
    if ('person' in next) setHp(next.person ?? null)
    if ('dept' in next) setHd(next.dept ?? null)
  }
  /** A click's holds: set them, or release them when they are the ones held. */
  const toggle = (next: Partial<Held>) => {
    const same = (Object.keys(next) as (keyof Held)[]).every((k) => held[k] === next[k])
    hold(Object.fromEntries(Object.keys(next).map((k) => [k, same ? null : next[k as keyof Held]])))
  }
  const anyHeld =
    held.status !== null || held.action !== null || held.person !== null || held.dept !== null || hm !== null

  // A held value the range does not have would empty the page with nothing saying why.
  useEffect(() => {
    if (!fullSel) return
    if (fullSel.ids.status === -1) setHs(null)
    if (fullSel.ids.action === -1) setHa(null)
    if (fullSel.ids.person === -1) setHp(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullSel])

  // ---- drill-through -------------------------------------------------------------
  const heldPeople = (h: Held): string[] => {
    if (!computed || !who) return []
    return computed.src.people.filter(
      (n, e) => who.inFilter[e] && (h.dept === null || who.dept[e] === h.dept) && (h.person === null || n === h.person),
    )
  }
  /** Touch detail, narrowed to exactly the rows a figure here counted. */
  const touches: TouchesFn = (what, d, h = held) => {
    // The tag says what was clicked and what the page was held to.
    const context = [
      hm ? monthYear(hm) : null,
      h.dept,
      h.person && !d.people ? h.person : null,
      d.status === undefined && h.status !== null ? h.status : null,
      d.action === undefined && h.action !== null ? h.action : null,
    ].filter((v): v is string => !!v)
    const label = context.length ? `${what} (${context.join(', ')})` : what
    return openTouches({
      employees: [],
      label,
      ...(hm ? { months: [hm] } : {}),
      drivers: {
        people: d.people ?? heldPeople(h),
        status: d.status === undefined ? (h.status ?? undefined) : d.status,
        action: d.action === undefined ? (h.action ?? undefined) : d.action,
        ...(d.claims ? { claims: d.claims } : {}),
        ...(d.repeatsOnly ? { repeatsOnly: true } : {}),
        lookBack: heldLookBack,
        label,
      },
    })
  }
  const claimsOf = (list: number[]) => {
    const all = computed!.src.claims
    return [...new Set(list)].map((c) => all[c]!)
  }
  const claims = (label: string, list: number[]) => openClaims({ employees: [], label, visits: claimsOf(list) })
  /** Every touch in the selection, whatever step is held: the base of "% of all touches" (#886). */
  const allTouches = useMemo(() => {
    if (!view$) return 0
    let n = 0
    for (const t of view$.byStepAll.values()) n += t.touches
    return n
  }, [view$])
  /** The claims one status / action pair's counted rows are on, under every other hold (#888). */
  const stepClaims = (s: number, a: number): number[] => {
    if (!computed || !sel) return []
    const r = computed.src.rows
    const keep = sel.keepOf({ status: true, action: true })
    const out: number[] = []
    for (let i = 0; i < r.n; i++)
      if (r.counted[i] && r.status[i] === s && r.action[i] === a && keep(i)) out.push(r.claim[i]!)
    return out
  }

  // ---- rendering ---------------------------------------------------------------------
  const base$ = view$ ? touchesPerClaim(view$.kpi) : null
  const fileTag = [...filters.department, ...filters.jobTitle, hm, held.dept, held.status, held.action, held.person]
    .filter((v): v is string => !!v)
    .map((v) => v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40))
  const file = (name: string) => [`touch-drivers_${name}`, ...fileTag].join('_') + '.csv'
  const scopeLabel = filters.jobTitle.length
    ? filters.jobTitle.join(', ')
    : filters.department.length
      ? filters.department.join(', ')
      : 'All of RCM'
  const chipLabel = { dept: 'Department', status: 'Status', action: 'Action', person: 'Person' } as const

  return (
    <>
      <h1 className="page-title">Touch drivers</h1>
      <p className="body-note" style={{ margin: '6px 0 0', maxWidth: 'none' }}>
        Touch detail, {monthSpan(months)}: why the team is touching claims, and where the same step keeps repeating
        without the claim moving.
        {lookBack.length > 0 && ` Repeats also look back to ${monthYear(lookBack[0]!)}, so ${monthYear(months[0]!)} does not read low.`}
      </p>

      {/* What the page is held to, pinned while scrolling. */}
      <div className="selbar td-holdbar" aria-live="polite">
        <div>
          <b>Holding this page to</b>
        </div>
        <div className="selbar-acts" style={{ alignItems: 'center' }}>
          {anyHeld ? (
            <>
              {hm !== null && (
                <span className="pill-sel">
                  Month: {monthYear(hm)}
                  <button type="button" title="Clear" aria-label="Clear month" onClick={() => setHm(null)}>
                    ×
                  </button>
                </span>
              )}
              {(['dept', 'status', 'action', 'person'] as const).map(
                (k) =>
                  held[k] !== null && (
                    <span key={k} className="pill-sel">
                      {chipLabel[k]}: {held[k] || '(blank)'}
                      <button type="button" title="Clear" aria-label={`Clear ${k}`} onClick={() => hold({ [k]: null })}>
                        ×
                      </button>
                    </span>
                  ),
              )}
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  hold({ status: null, action: null, person: null, dept: null })
                  setHm(null)
                }}
              >
                Clear all
              </button>
            </>
          ) : (
            <span style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>
              nothing: click a month, a card, a bar or a row
            </span>
          )}
        </div>
      </div>

      <div className="td-tabs" role="tablist" aria-label="Touch drivers">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`td-tab-${t.key}`}
            aria-selected={tab === t.key}
            aria-controls="td-panel"
            onClick={() => setTab(t.key === 'overview' ? '' : t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!view$ || !computed || !training || !byMonth || !who || !sel || !full ? (
        <p className="body-note" style={{ marginTop: 16 }}>
          {error ?? 'Loading touches…'}
        </p>
      ) : (
        <div role="tabpanel" id="td-panel" aria-labelledby={`td-tab-${tab}`}>
          {tab === 'overview' && (
            <>
              {/* ---- headline ---- */}
              <section className="card" style={{ marginTop: 16 }} aria-label="Repeat touches">
                <div className="td-headline">
                  <div style={{ minWidth: 0 }}>
                    <span className="eyebrow">Repeat touches</span>
                    <div className="hero-figure" style={{ color: 'var(--warn-ink)' }}>
                      {pct(repeatRate(view$.kpi))} <span className="td-hero-note">of touches repeat a step</span>
                    </div>
                    <p className="body-note" style={{ margin: '6px 0 0' }}>
                      {view$.kpi.touches ? (
                        <>
                          <Link
                            title="Open these repeat touches in Touch detail"
                            onClick={() => touches('Repeat touches', { repeatsOnly: true })}
                          >
                            {int(view$.kpi.repeats)}
                          </Link>{' '}
                          of {int(view$.kpi.touches)} touches{' '}
                          {anyHeld || filters.department.length || filters.jobTitle.length ? 'in this selection' : 'across RCM'}{' '}
                          repeated a step the same person had already taken on the same claim, with the status
                          unchanged. Some are scheduled follow-ups.
                        </>
                      ) : (
                        'No touches match these filters.'
                      )}
                    </p>
                    <div className="td-stats">
                      <Stat
                        label="Touches"
                        value={int(view$.kpi.touches)}
                        note={
                          <Link title="Open these touches in Touch detail" onClick={() => touches('Touch drivers', {})}>
                            open in Touch detail
                          </Link>
                        }
                      />
                      <Stat label="Claims touched" value={int(view$.kpi.claims)} />
                      <Stat label="Touches per claim" value={dec(base$)} />
                      <Stat label="Stuck claims" value={int(view$.stuck.length)} note="5+ touches, no status change" />
                      <Stat label="Duplicate entries" value={int(view$.kpi.dups)} />
                      <Stat
                        label="Blank notes"
                        value={pct(view$.kpi.touches ? view$.kpi.blanks / view$.kpi.touches : null)}
                        note={`${int(view$.kpi.blanks)} under 5 characters`}
                      />
                    </div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <span className="eyebrow">By month · click one to hold it</span>
                    <div style={{ marginTop: 8 }}>
                      <StackColumns
                        height={250}
                        categories={byMonth.map(([m]) => ({ key: m, label: MONTH_SHORT(m) }))}
                        series={[
                          {
                            key: 'r',
                            label: 'Repeat touches',
                            color: 'var(--serious)',
                            fg: 'var(--ord1-fg)',
                            values: byMonth.map(([, t]) => t?.repeats ?? 0),
                          },
                          {
                            key: 'f',
                            label: 'First-time touches',
                            color: 'var(--ord1)',
                            fg: 'var(--ord1-fg)',
                            values: byMonth.map(([, t]) => (t ? t.touches - t.repeats : 0)),
                          },
                        ]}
                        selected={hm}
                        onSelect={(m) => setHm(hm === m ? null : m)}
                        partial={openMonths}
                      />
                    </div>
                    {lookBack.length === 0 && months.length > 1 && (
                      <p style={{ margin: '6px 0 0', fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>
                        {monthYear(months[0]!)} reads low: the touch file has no earlier month to look back to, so a
                        repeat of a touch made before it is counted as a first touch.
                      </p>
                    )}
                  </div>
                </div>
              </section>

              {/* ---- training ---- */}
              <section className="card" style={{ marginTop: 16 }}>
                <h2 className="card-heading">Where to focus training</h2>
                <p className="body-note" style={{ margin: '4px 0 0', maxWidth: 'none' }}>
                  {scopeLabel}, {monthSpan(months)}. From the top bar's filters only. Click a card to hold the page to
                  it.
                </p>
                <div className="td-cards">
                  {trainingCards(training, full.src).map((c, i) => {
                    const h = c.hold
                    const on = !!h && (Object.keys(h) as (keyof Held)[]).every((k) => held[k] === h[k])
                    return h ? (
                      <button key={i} type="button" className="td-card" aria-pressed={on} onClick={() => toggle(h)}>
                        <span className="eyebrow">{c.title}</span>
                        <span>{c.body}</span>
                        <span className="td-card-go">{on ? 'Holding · click to release' : 'Hold the page to this →'}</span>
                      </button>
                    ) : (
                      <div key={i} className="td-card">
                        <span className="eyebrow">{c.title}</span>
                        <span>{c.body}</span>
                      </div>
                    )
                  })}
                </div>
              </section>

              <Fold id="howOpen" title="How to read this page" startOpen={false}>
                <HowToRead />
              </Fold>
            </>
          )}

          {tab === 'steps' && (
            <>
              <TopBars
                title="Where the waste is, by status / action"
                noun="status / action pairs"
                note="The 15 status / action pairs with the most repeat touches. Click a bar to hold the page to that pair. The chart ignores the held pair, so you can click from one bar to the next."
                toggle={toggle}
                bars={[...view$.byStepAll].map(([k, t]) => {
                  const s = Math.floor(k / view$.A)
                  const a = k % view$.A
                  const st = computed.src.statuses[s]!
                  const ac = computed.src.actions[a]!
                  return {
                    key: String(k),
                    label: `${st || '(blank)'} → ${ac || '(blank)'}`,
                    t,
                    hold: { status: st, action: ac },
                    on: ids?.status === s && ids?.action === a,
                  }
                })}
              />
              <section style={{ marginTop: 16 }}>
                <StepTable
                  view={view$}
                  src={computed.src}
                  base={base$}
                  total={allTouches}
                  q={stepQ}
                  setQ={setStepQ}
                  held={ids}
                  toggle={(status, action) => toggle({ status, action })}
                  touches={touches}
                  claims={claims}
                  claimsOf={stepClaims}
                  toEmployees={
                    ids && ids.status !== null && ids.action !== null ? () => setTab('people') : undefined
                  }
                  file={file}
                />
              </section>
            </>
          )}

          {tab === 'people' && (
            <>
            <TopBars
              title="Who repeats the same step, by employee"
              noun="employees"
              note="The 15 employees with the most repeat touches. Click a bar to see that employee alone below, with their repeated touches by status / action. The chart ignores the held employee, so you can click from one bar to the next."
              toggle={toggle}
              bars={[...view$.byPerson].map(([e, t]) => {
                const n = computed.src.people[e]!
                return { key: n, label: n, t, hold: { person: n }, on: held.person === n }
              })}
            />
            {held.person !== null && ids?.person != null && ids.person >= 0 ? (
              <section style={{ marginTop: 16 }}>
                <StepTable
                  view={view$}
                  src={computed.src}
                  base={base$}
                  total={allTouches}
                  q={stepQ}
                  setQ={setStepQ}
                  held={ids}
                  toggle={(status, action) => toggle({ status, action })}
                  touches={touches}
                  claims={claims}
                  claimsOf={stepClaims}
                  file={file}
                  title={`${held.person}: repeated touches by status / action`}
                  note={
                    <>
                      Only {held.person}'s touches, one row per status / action pair, ranked by repeat touches.{' '}
                      <button type="button" className="jump-link" onClick={() => hold({ person: null })}>
                        Back to every employee
                      </button>
                    </>
                  }
                  storageKey="touch-drivers.person-steps"
                />
              </section>
            ) : (
            <section style={{ marginTop: 16 }}>
              <PeopleTable
                view={view$}
                src={computed.src}
                who={who}
                loops={sel.loopsFor({ person: true })}
                base={base$}
                q={personQ}
                setQ={setPersonQ}
                heldPerson={held.person}
                toggle={(person) => toggle({ person })}
                touches={touches}
                claims={claims}
                rowsOf={(e) => personClaims(computed, e, sel.keepOf({ person: true }))}
                file={file}
              />
            </section>
            )}
            </>
          )}

          {tab === 'moving' && (
            <section style={{ marginTop: 16 }}>
              <NotMoving
                key={view}
                view={view}
                setView={(v) => {
                  write('cpc.td.view', v)
                  setView(v)
                }}
                loops={view$.loops}
                stuck={view$.stuck}
                src={computed.src}
                who={who}
                held={held}
                ids={ids}
                toggle={toggle}
                touches={touches}
                claims={claims}
                base={base.map((p) => p.employee)}
                file={file}
              />
            </section>
          )}
        </div>
      )}
    </>
  )
}

const MONTH_SHORT = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleString('en-US', { month: 'short' })

/** The definitions, at the foot of the Overview tab. */
function HowToRead() {
  return (
    <dl className="td-defs">
      <dt>Touch</dt>
      <dd>
        One row in Touch detail: a person logged a status and an action on a claim. Touches are plain counts; payer
        weights apply to claims, not touches.
      </dd>
      <dt>Claim</dt>
      <dd>A visit number at a facility.</dd>
      <dt>Step</dt>
      <dd>A status together with the action taken on it.</dd>
      <dt>Repeat touch</dt>
      <dd>
        The same person already took the same step on the same claim earlier in the months picked (and the month
        before), with the status unchanged. The first time is not a repeat.
      </dd>
      <dt>Not every repeat is waste</dt>
      <dd>
        Scheduled follow-ups, such as monthly family income or a 30-day pending check, count as repeats too. Check a
        step's normal cadence before treating its repeats as wasted time.
      </dd>
      <dt>Loop</dt>
      <dd>One person taking the same step on the same claim three or more times in the months picked.</dd>
      <dt>Stuck claim</dt>
      <dd>
        Five or more touches in the months picked and the status never changed. It counts under the person who touched
        it most, and their department.
      </dd>
      <dt>Duplicate entry</dt>
      <dd>An identical row (same person, claim, date, status, action and note) logged more than once.</dd>
      <dt>Blank note</dt>
      <dd>A note under 5 characters. Thin notes make the next touch start from scratch.</dd>
      <dt>Holding a month</dt>
      <dd>
        Clicking a month treats it as its own range: repeats look back to the month before, and loops and stuck claims
        are that month's.
      </dd>
      <dt>Links</dt>
      <dd>
        Underlined touch counts open exactly those touches in Touch detail; underlined claim counts open exactly those
        claims in Claim detail. Claim detail lists the claims billed or resolved in the app's months, so a claim billed
        earlier may not be there; it says how many.
      </dd>
      <dt>Not the Touch profile</dt>
      <dd>
        This page counts the touches people made, by touch date. The Touch profile counts the touches on the claims they
        resolved, by resolution month. The two do not tie.
      </dd>
      <dt>Whose touches</dt>
      <dd>The people the filters and your access allow. A claim is judged on their touches only.</dd>
    </dl>
  )
}

/** How many bars a chart shows. */
const BARS_SHOWN = 15

/** One bar: what it is, its tally, the hold a click sets, and whether that hold is on. */
interface Bar {
  key: string
  label: string
  t: Tally
  hold: Partial<Held>
  on: boolean
}

/**
 * The top steps or people by repeat touches, as ranked bars (Yehuda, 2026-10-21: one
 * fixed view per chart, no switches). The chip is the repeat rate; a click holds the
 * page to the bar and a second click releases it. A held bar stays on the chart when it
 * is not in the top few.
 */
function TopBars({ title, note, noun, bars, toggle }: {
  title: string
  note: ReactNode
  noun: string
  bars: Bar[]
  toggle: (h: Partial<Held>) => void
}) {
  const ranked = bars
    .filter((b) => b.t.repeats > 0)
    .sort((x, y) => y.t.repeats - x.t.repeats || y.t.touches - x.t.touches)
  const shown = ranked.slice(0, BARS_SHOWN)
  const held = ranked.find((b) => b.on)
  if (held && !shown.includes(held)) shown.push(held)
  const total = ranked.reduce((n, b) => n + b.t.repeats, 0)
  const share = total ? shown.reduce((n, b) => n + b.t.repeats, 0) / total : 0
  const max = Math.max(1, ...shown.map((b) => b.t.repeats))
  return (
    <section className="card" style={{ marginTop: 16 }}>
      <h2 className="card-heading">{title}</h2>
      <p className="body-note" style={{ margin: '4px 0 0', maxWidth: 'none', fontSize: 'var(--t-micro)' }}>
        {note}
      </p>
      {shown.length === 0 ? (
        <p className="body-note" style={{ marginTop: 12 }}>
          No repeat touches match these filters.
        </p>
      ) : (
        <ol className="td-bars">
          {/* Review #892: say what the two figures are. */}
          <li className="td-bars-head" aria-hidden="true">
            <span />
            <span />
            <span>Repeat touches</span>
            <span>Repeat rate</span>
          </li>
          {shown.map((b) => {
            const rate = repeatRate(b.t)
            return (
              <li key={b.key}>
                <button
                  type="button"
                  aria-pressed={b.on}
                  title={`${b.label}: ${int(b.t.repeats)} repeats of ${int(b.t.touches)} touches (${pct(rate)}). Click to ${b.on ? 'release' : 'hold the page to it'}.`}
                  onClick={() => toggle(b.hold)}
                >
                  <span className="td-bar-label">{b.label}</span>
                  <span className="td-bar-track">
                    <span className="td-bar-fill" style={{ width: `${(100 * b.t.repeats) / max}%` }} />
                  </span>
                  <span className="td-bar-n mono">{int(b.t.repeats)}</span>
                  <span className={`pts-chip ${repeatBand(rate) ?? ''}`}>{pct(rate)}</span>
                </button>
              </li>
            )
          })}
        </ol>
      )}
      {ranked.length > BARS_SHOWN && (
        <p style={{ margin: '8px 0 0', fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>
          These {shown.length} of {int(ranked.length)} {noun} hold {pct(share, 0)} of the repeat touches. All of them
          are in the list below.
        </p>
      )}
    </section>
  )
}

/** The claims a person's counted rows are on, for the people table's Claims link. */
function personClaims(computed: { src: DriverSource }, e: number, keep: (i: number) => boolean): number[] {
  const r = computed.src.rows
  const out: number[] = []
  for (let i = 0; i < r.n; i++) if (r.counted[i] && r.person[i] === e && keep(i)) out.push(r.claim[i]!)
  return out
}

type ViewData = {
  kpi: Tally
  byStep: Map<number, Tally>
  byPerson: Map<number, Tally>
  loops: Loop[]
  stuck: Stuck[]
  A: number
}
type TouchesFn = (label: string, d: Partial<Omit<TouchDriversDrill, 'lookBack' | 'label'>>, h?: Held) => void
type ClaimsFn = (label: string, list: number[]) => void

/** "Where the waste is": one row per step. */
function StepTable({
  view,
  src,
  base,
  total,
  q,
  setQ,
  held,
  toggle,
  touches,
  claims,
  claimsOf,
  toEmployees,
  file,
  title = 'Every status / action pair',
  note = 'Each status with the action taken on it, ranked by repeat touches. A high repeat rate with many touches per claim means the same step keeps being taken without the claim moving.',
  storageKey = 'touch-drivers.steps',
}: {
  view: ViewData
  src: DriverSource
  base: number | null
  /** Every touch in the selection: the base of "% of all touches". */
  total: number
  q: string
  setQ: (v: string) => void
  held: { status: number | null; action: number | null } | null
  toggle: (status: string, action: string) => void
  touches: TouchesFn
  claims: ClaimsFn
  claimsOf: (s: number, a: number) => number[]
  /** With a pair held: go to the By employee tab, held to it (#889). */
  toEmployees?: () => void
  file: (n: string) => string
  title?: string
  note?: ReactNode
  storageKey?: string
}) {
  const all = useMemo(
    () =>
      [...view.byStep]
        .map(([k, t]) => {
          const s = Math.floor(k / view.A)
          const a = k % view.A
          return { k, s, a, status: src.statuses[s]!, action: src.actions[a]!, t }
        })
        .sort((a, b) => b.t.repeats - a.t.repeats || b.t.touches - a.t.touches),
    [view, src],
  )
  const needle = q.trim().toLowerCase()
  const rows = needle ? all.filter((r) => `${r.status}\n${r.action}`.toLowerCase().includes(needle)) : all
  type Row = (typeof rows)[number]
  const max = Math.max(1, ...rows.slice(0, SHOWN).map((r) => r.t.repeats))
  const name = (r: Row) => `${r.status || '(blank)'} → ${r.action || '(blank)'}`
  const drill = (r: Row, repeatsOnly: boolean) =>
    touches(`Step: ${name(r)}`, { status: r.status, action: r.action, ...(repeatsOnly ? { repeatsOnly } : {}) })
  return (
    <TableCard<Row>
      title={title}
      note={note}
      head={
        toEmployees && (
          <div style={{ marginTop: 6 }}>
            <button type="button" className="jump-link" onClick={toEmployees}>
              See this pair by employee →
            </button>
          </div>
        )
      }
      search={{ value: q, set: setQ, placeholder: 'Find a status or action' }}
      count={needle ? `${int(rows.length)} of ${int(all.length)} pairs match` : `${int(all.length)} pairs`}
      storageKey={storageKey}
      filename={file(`steps${needle ? `_${needle.replace(/[^a-z0-9]+/g, '-')}` : ''}`)}
      rows={rows}
      rowKey={(r) => String(r.k)}
      selectedKey={held && held.status !== null && held.action !== null ? String(held.status * view.A + held.action) : null}
      onRowClick={(r) => toggle(r.status, r.action)}
      initialSort="r"
      sortParam="sstep"
      empty="No steps match."
      exportColumns={[
        { header: 'Status', value: (r) => r.status },
        { header: 'Action', value: (r) => r.action },
        { header: 'Repeat touches', value: (r) => r.t.repeats },
        { header: 'Repeats, % of all touches', value: (r) => shareOfTouches(r.t, total, 'repeats') },
        { header: 'Touches', value: (r) => r.t.touches },
        { header: 'Touches, % of all touches', value: (r) => shareOfTouches(r.t, total, 'touches') },
        { header: 'Claims', value: (r) => r.t.claims },
        { header: 'Touches per claim', value: (r) => touchesPerClaim(r.t) },
        { header: 'Repeat rate', value: (r) => repeatRate(r.t) },
      ]}
      columns={[
        { key: 'st', header: 'Status', width: 260, sort: (r) => r.status, render: (r) => <span className="td-trunc" style={{ maxWidth: 260 }} title={r.status}>{r.status || '(blank)'}</span> },
        { key: 'ac', header: 'Action', width: 220, sort: (r) => r.action, render: (r) => <span className="td-trunc" style={{ maxWidth: 220 }} title={r.action}>{r.action || '(blank)'}</span> },
        {
          key: 'r',
          header: 'Repeat touches',
          align: 'right',
          band: true,
          sort: (r) => r.t.repeats,
          render: (r) => (
            <RepeatCell
              n={r.t.repeats}
              max={max}
              link={<Link title="Open these repeat touches in Touch detail" onClick={() => drill(r, true)}>{int(r.t.repeats)}</Link>}
            />
          ),
        },
        {
          // Review #886, both readings (Yehuda, 2026-10-22): this one is the repeats'.
          key: 'share',
          header: 'Repeats, % of all touches',
          title: "This pair's repeat touches as a share of every touch in the selection",
          align: 'right',
          sort: (r) => shareOfTouches(r.t, total, 'repeats'),
          render: (r) => mono(pct(shareOfTouches(r.t, total, 'repeats'))),
        },
        {
          key: 't',
          header: 'Touches',
          align: 'right',
          sort: (r) => r.t.touches,
          render: (r) => <Link title="Open these touches in Touch detail" onClick={() => drill(r, false)}>{int(r.t.touches)}</Link>,
        },
        {
          // Review #886, the other reading: all of this pair's touches, repeats or not.
          key: 'tshare',
          header: 'Touches, % of all touches',
          title: "This pair's touches, repeats or not, as a share of every touch in the selection",
          align: 'right',
          sort: (r) => shareOfTouches(r.t, total, 'touches'),
          render: (r) => mono(pct(shareOfTouches(r.t, total, 'touches'))),
        },
        {
          // Review #888: open the claims themselves.
          key: 'c',
          header: 'Claims',
          align: 'right',
          sort: (r) => r.t.claims,
          render: (r) => (
            <Link title="Open these claims in Claim detail" onClick={() => claims(`Claims: ${name(r)}`, claimsOf(r.s, r.a))}>
              {int(r.t.claims)}
            </Link>
          ),
        },
        {
          key: 'pc',
          header: 'Per claim',
          align: 'right',
          sort: (r) => touchesPerClaim(r.t),
          render: (r) => <Chip band={perClaimBand(touchesPerClaim(r.t), base)}>{dec(touchesPerClaim(r.t))}</Chip>,
        },
        {
          key: 'rr',
          header: 'Repeat rate',
          align: 'right',
          sort: (r) => repeatRate(r.t),
          render: (r) => <Chip band={repeatBand(repeatRate(r.t))}>{pct(repeatRate(r.t))}</Chip>,
        },
      ]}
    />
  )
}

/** Rate pills only for people with this many touches; fewer is noise. */
const PILL_MIN_TOUCHES = 50

/** "Who repeats the same step": one row per person. */
function PeopleTable({
  view,
  src,
  who,
  loops,
  base,
  q,
  setQ,
  heldPerson,
  toggle,
  touches,
  claims,
  rowsOf,
  file,
}: {
  view: ViewData
  src: DriverSource
  who: { dept: string[]; title: string[] }
  loops: Loop[]
  base: number | null
  q: string
  setQ: (v: string) => void
  heldPerson: string | null
  toggle: (person: string) => void
  touches: TouchesFn
  claims: ClaimsFn
  rowsOf: (e: number) => number[]
  file: (n: string) => string
}) {
  const all = useMemo(() => {
    // Each person's loops and their most looped step.
    const lp = new Map<number, { n: number; steps: Map<number, number> }>()
    for (const l of loops) {
      let x = lp.get(l.person)
      if (!x) lp.set(l.person, (x = { n: 0, steps: new Map() }))
      x.n++
      const k = l.status * view.A + l.action
      x.steps.set(k, (x.steps.get(k) ?? 0) + 1)
    }
    return [...view.byPerson].map(([e, t]) => {
      const x = lp.get(e)
      let top: { status: string; action: string; n: number } | null = null
      if (x) {
        let bestK = -1
        let best = 0
        for (const [k, n] of x.steps) if (n > best) (bestK = k), (best = n)
        top = { status: src.statuses[Math.floor(bestK / view.A)]!, action: src.actions[bestK % view.A]!, n: best }
      }
      return {
        e,
        name: src.people[e]!,
        title: who.title[e]!,
        dept: who.dept[e]!,
        t,
        perDay: t.days ? t.touches / t.days : null,
        loops: x?.n ?? 0,
        top,
      }
    })
  }, [view, src, who, loops])
  const needle = q.trim().toLowerCase()
  const rows = needle ? all.filter((r) => r.name.toLowerCase().includes(needle) || r.title.toLowerCase().includes(needle)) : all
  type Row = (typeof rows)[number]
  const blankShare = (r: Row) => (r.t.touches ? r.t.blanks / r.t.touches : null)
  const stepName = (r: Row) => (r.top ? `${r.top.status || '(blank)'} → ${r.top.action || '(blank)'}` : '')
  return (
    <TableCard<Row>
      title="Everyone"
      note={`Every person, ranked by repeat touches, with each person's most looped step. Click a row to hold the page to that person. Rate pills are shown for people with ${PILL_MIN_TOUCHES} or more touches; per day, blank notes and duplicates are in the export.`}
      search={{ value: q, set: setQ, placeholder: 'Find an employee' }}
      count={needle ? `${int(rows.length)} of ${int(all.length)} people match` : `${int(all.length)} people`}
      storageKey="touch-drivers.people"
      filename={file('people')}
      rows={rows}
      rowKey={(r) => `p:${r.name}`}
      selectedKey={heldPerson === null ? null : `p:${heldPerson}`}
      onRowClick={(r) => toggle(r.name)}
      initialSort="r"
      sortParam="sppl"
      empty="No people match these filters."
      exportColumns={[
        { header: 'Employee', value: (r) => r.name },
        { header: 'Job title', value: (r) => r.title },
        { header: 'Department', value: (r) => r.dept },
        { header: 'Touches', value: (r) => r.t.touches },
        { header: 'Claims', value: (r) => r.t.claims },
        { header: 'Touches per claim', value: (r) => touchesPerClaim(r.t) },
        { header: 'Touches per day', value: (r) => r.perDay },
        { header: 'Repeat touches', value: (r) => r.t.repeats },
        { header: 'Repeat rate', value: (r) => repeatRate(r.t) },
        { header: 'Loops', value: (r) => r.loops },
        { header: 'Most looped status', value: (r) => r.top?.status ?? '' },
        { header: 'Most looped action', value: (r) => r.top?.action ?? '' },
        { header: 'Claims looped on that step', value: (r) => r.top?.n ?? '' },
        { header: 'Blank notes', value: blankShare },
        { header: 'Duplicate entries', value: (r) => r.t.dups },
      ]}
      columns={[
        {
          key: 'name',
          header: 'Employee',
          width: 200,
          sort: (r) => r.name,
          render: (r) => <PersonCell name={r.name} meta={[r.title, r.dept].filter(Boolean).join(' · ')} />,
        },
        {
          key: 't',
          header: 'Touches',
          align: 'right',
          sort: (r) => r.t.touches,
          render: (r) => (
            <Link title="Open their touches in Touch detail" onClick={() => touches(r.name, { people: [r.name] })}>
              {int(r.t.touches)}
            </Link>
          ),
        },
        {
          key: 'c',
          header: 'Claims',
          align: 'right',
          sort: (r) => r.t.claims,
          render: (r) => (
            <Link title="Open these claims in Claim detail" onClick={() => claims(`Claims ${r.name} touched`, rowsOf(r.e))}>
              {int(r.t.claims)}
            </Link>
          ),
        },
        {
          key: 'pc',
          header: 'Per claim',
          align: 'right',
          sort: (r) => touchesPerClaim(r.t),
          render: (r) => <Chip band={perClaimBand(touchesPerClaim(r.t), base)}>{dec(touchesPerClaim(r.t))}</Chip>,
        },
        {
          key: 'r',
          header: 'Repeat touches',
          align: 'right',
          band: true,
          sort: (r) => r.t.repeats,
          render: (r) => (
            <Link title="Open their repeat touches in Touch detail" onClick={() => touches(`${r.name}: repeats`, { people: [r.name], repeatsOnly: true })}>
              {int(r.t.repeats)}
            </Link>
          ),
        },
        {
          key: 'rr',
          header: 'Repeat rate',
          align: 'right',
          sort: (r) => repeatRate(r.t),
          render: (r) =>
            r.t.touches >= PILL_MIN_TOUCHES ? (
              <Chip band={repeatBand(repeatRate(r.t))}>{pct(repeatRate(r.t))}</Chip>
            ) : (
              <span className="mono" style={{ color: 'var(--ink-3)' }}>
                {pct(repeatRate(r.t))}
              </span>
            ),
        },
        { key: 'lp', header: 'Loops', align: 'right', sort: (r) => r.loops, render: (r) => mono(int(r.loops)) },
        {
          key: 'top',
          header: 'Most looped step',
          width: 260,
          sort: (r) => r.top?.n ?? 0,
          render: (r) =>
            r.top ? (
              <span className="td-trunc" style={{ maxWidth: 260 }} title={stepName(r)}>
                {stepName(r)} <span style={{ color: 'var(--ink-3)' }}>({r.top.n} claims)</span>
              </span>
            ) : (
              <span style={{ color: 'var(--ink-3)' }}>none</span>
            ),
        },
      ]}
    />
  )
}

/** "Claims that aren't moving": loops by person and step, or stuck claims by department. */
function NotMoving({
  view,
  setView,
  loops,
  stuck,
  src,
  who,
  held,
  ids,
  toggle,
  touches,
  claims,
  base,
  file,
}: {
  view: 'loops' | 'stuck'
  setView: (v: 'loops' | 'stuck') => void
  loops: Loop[]
  stuck: Stuck[]
  src: DriverSource
  who: { dept: string[] }
  held: Held
  ids: { status: number | null; action: number | null; person: number | null } | null
  toggle: (h: Partial<Held>) => void
  touches: TouchesFn
  claims: ClaimsFn
  base: string[]
  file: (n: string) => string
}) {
  const toggleBar = (
    <div role="radiogroup" aria-label="View" className="td-seg" style={{ marginTop: 10 }}>
      {(
        [
          ['loops', 'Loops by person and step'],
          ['stuck', 'Stuck claims by department'],
        ] as const
      ).map(([v, label]) => (
        <button key={v} type="button" role="radio" aria-checked={view === v} onClick={() => view !== v && setView(v)}>
          {label}
        </button>
      ))}
    </div>
  )
  const loopRows = useMemo(() => {
    const g = new Map<string, { e: number; s: number; a: number; claims: number[]; t: number; max: number; span: number; fac: Set<string> }>()
    for (const l of loops) {
      const k = `${l.person},${l.status},${l.action}`
      let x = g.get(k)
      if (!x) g.set(k, (x = { e: l.person, s: l.status, a: l.action, claims: [], t: 0, max: 0, span: 0, fac: new Set() }))
      x.claims.push(l.claim)
      x.t += l.n
      x.max = Math.max(x.max, l.n)
      x.span += l.span
      x.fac.add(src.claims[l.claim]!.f)
    }
    return [...g.entries()]
      .map(([k, x]) => ({ k, ...x, name: src.people[x.e]!, status: src.statuses[x.s]!, action: src.actions[x.a]!, avgDays: x.span / x.claims.length }))
      .sort((a, b) => b.t - a.t || b.claims.length - a.claims.length)
  }, [loops, src])
  const stuckRows = useMemo(() => {
    const g = new Map<string, { dept: string; s: number; claims: number[]; t: number }>()
    for (const s of stuck) {
      const dept = who.dept[s.owner]!
      const k = `${dept}\u0001${s.status}`
      let x = g.get(k)
      if (!x) g.set(k, (x = { dept, s: s.status, claims: [], t: 0 }))
      x.claims.push(s.claim)
      x.t += s.n
    }
    return [...g.entries()]
      .map(([k, x]) => ({ k, ...x, status: src.statuses[x.s]! }))
      .sort((a, b) => b.claims.length - a.claims.length || b.t - a.t)
  }, [stuck, src, who])

  if (view === 'loops') {
    type Row = (typeof loopRows)[number]
    const step = (r: Row) => `${r.status || '(blank)'} → ${r.action || '(blank)'}`
    return (
      <TableCard<Row>
        title="Claims that aren't moving"
        note="One row per person and step: how many claims they took that step on three or more times. Claims open exactly those claims in Claim detail; touches open exactly those touches in Touch detail."
        head={toggleBar}
        storageKey="touch-drivers.loops"
        filename={file('loops')}
        rows={loopRows}
        rowKey={(r) => r.k}
        selectedKey={ids && ids.person !== null && ids.status !== null && ids.action !== null ? `${ids.person},${ids.status},${ids.action}` : null}
        onRowClick={(r) => toggle({ person: r.name, status: r.status, action: r.action })}
        initialSort="t"
        empty="No loops match these filters."
        exportColumns={[
          { header: 'Employee', value: (r) => r.name },
          { header: 'Department', value: (r) => who.dept[r.e] ?? '' },
          { header: 'Status', value: (r) => r.status },
          { header: 'Action', value: (r) => r.action },
          { header: 'Claims looped', value: (r) => r.claims.length },
          { header: 'Touches', value: (r) => r.t },
          { header: 'Most touches on one claim', value: (r) => r.max },
          { header: 'Average days', value: (r) => Math.round(r.avgDays) },
          { header: 'Facilities', value: (r) => [...r.fac].join('; ') },
        ]}
        columns={[
          {
            key: 'name',
            header: 'Employee',
            width: 200,
            sort: (r) => r.name,
            render: (r) => <PersonCell name={r.name} meta={r.fac.size === 1 ? '1 facility' : `${r.fac.size} facilities`} />,
          },
          { key: 'step', header: 'Status → action', width: 300, sort: step, render: (r) => <span className="td-trunc" style={{ maxWidth: 300 }} title={step(r)}>{step(r)}</span> },
          {
            key: 'c',
            header: 'Claims looped',
            align: 'right',
            sort: (r) => r.claims.length,
            render: (r) => (
              <Link title="Open exactly these claims in Claim detail" onClick={() => claims(`${r.name}: ${step(r)}, looped`, r.claims)}>
                {int(r.claims.length)}
              </Link>
            ),
          },
          {
            key: 't',
            header: 'Touches',
            align: 'right',
            band: true,
            sort: (r) => r.t,
            render: (r) => (
              <Link
                title="Open exactly these touches in Touch detail"
                onClick={() =>
                  touches(`${r.name}: ${step(r)}, looped`, {
                    people: [r.name],
                    status: r.status,
                    action: r.action,
                    claims: [...new Set(r.claims)].map((c) => src.claims[c]!),
                  })
                }
              >
                {int(r.t)}
              </Link>
            ),
          },
          { key: 'max', header: 'Most on one claim', align: 'right', sort: (r) => r.max, render: (r) => mono(int(r.max)) },
          { key: 'd', header: 'Avg days', align: 'right', sort: (r) => r.avgDays, render: (r) => mono(int(r.avgDays)) },
        ]}
      />
    )
  }
  type Row = (typeof stuckRows)[number]
  return (
    <TableCard<Row>
      title="Claims that aren't moving"
      note="Claims touched five or more times with no status change, by department and status. Each claim counts under the department of the person who touched it most. Both counts open exactly those claims and their touches."
      head={toggleBar}
      storageKey="touch-drivers.stuck-claims"
      filename={file('stuck-claims')}
      rows={stuckRows}
      rowKey={(r) => r.k}
      selectedKey={held.dept !== null && ids && ids.status !== null ? `${held.dept}\u0001${ids.status}` : null}
      onRowClick={(r) => toggle({ dept: r.dept, status: r.status })}
      initialSort="c"
      empty="No stuck claims match these filters."
      exportColumns={[
        { header: 'Department', value: (r) => r.dept },
        { header: 'Status', value: (r) => r.status },
        { header: 'Stuck claims', value: (r) => r.claims.length },
        { header: 'Touches', value: (r) => r.t },
        { header: 'Touches per claim', value: (r) => r.t / r.claims.length },
      ]}
      columns={[
        { key: 'dept', header: 'Department', width: 200, sort: (r) => r.dept, render: (r) => r.dept || '(none)' },
        { key: 'st', header: 'Status', width: 300, sort: (r) => r.status, render: (r) => <span className="td-trunc" style={{ maxWidth: 300 }} title={r.status}>{r.status || '(blank)'}</span> },
        {
          key: 'c',
          header: 'Stuck claims',
          align: 'right',
          band: true,
          sort: (r) => r.claims.length,
          render: (r) => (
            <Link title="Open exactly these claims in Claim detail" onClick={() => claims(`Stuck: ${r.dept}, ${r.status}`, r.claims)}>
              {int(r.claims.length)}
            </Link>
          ),
        },
        {
          key: 't',
          header: 'Touches',
          align: 'right',
          sort: (r) => r.t,
          render: (r) => (
            <Link
              title="Open exactly these touches in Touch detail"
              onClick={() =>
                touches(
                  `Stuck: ${r.dept}, ${r.status}`,
                  {
                    // Every touch on the claims counted, whoever made it, any action.
                    people: base,
                    status: r.status,
                    claims: r.claims.map((c) => src.claims[c]!),
                  },
                  { ...held, action: null, person: null, dept: null },
                )
              }
            >
              {int(r.t)}
            </Link>
          ),
        },
        { key: 'pc', header: 'Per claim', align: 'right', sort: (r) => r.t / r.claims.length, render: (r) => mono(dec(r.t / r.claims.length, 1)) },
      ]}
    />
  )
}

/** A training card: what to look at, and the hold that shows it. */
interface TrainingCard {
  title: string
  body: ReactNode
  hold?: Partial<Held>
}

/**
 * Where to focus training (HANDOFF.md §5), as cards that hold the page: the biggest
 * repeat step, the next two, the three people with most repeats, the stuck claims, data
 * hygiene. From the top bar's filters and range only.
 */
function trainingCards(
  t: { steps: [number, Tally][]; people: [number, Tally][]; stuck: Stuck[]; kpi: Tally; A: number },
  src: DriverSource,
): TrainingCard[] {
  const out: TrainingCard[] = []
  const nm = (k: number) => ({ s: src.statuses[Math.floor(k / t.A)]!, a: src.actions[k % t.A]! })
  const pair = (k: number) => {
    const x = nm(k)
    return `${x.s || '(blank)'} → ${x.a || '(blank)'}`
  }
  const [first, second, third] = t.steps
  if (first) {
    const [k, v] = first
    const x = nm(k)
    out.push({
      title: 'Biggest repeat',
      body: (
        <>
          <b>{pair(k)}</b>: {int(v.repeats)} repeat touches, {pct(repeatRate(v))} of that step.
          {restatesStatus(x.s, x.a) && ' The action only restates the status, so the touch records nothing new.'}
        </>
      ),
      hold: { status: x.s, action: x.a },
    })
  }
  for (const next of [second, third]) {
    if (!next) continue
    const [k, v] = next
    const x = nm(k)
    out.push({
      title: 'Next',
      body: (
        <>
          <b>{pair(k)}</b>: {int(v.repeats)} repeat touches, {pct(repeatRate(v))} of that step.
        </>
      ),
      hold: { status: x.s, action: x.a },
    })
  }
  const top = t.people
    .filter(([, v]) => v.repeats > 0)
    .sort(([, a], [, b]) => b.repeats - a.repeats)
    .slice(0, 3)
  for (const [i, [e, v]] of top.entries())
    out.push({
      title: i === 0 ? 'Most repeat touches' : `Repeat touches, #${i + 1}`,
      body: (
        <>
          <b>{src.people[e]}</b>: {int(v.repeats)}, {pct(repeatRate(v))} of their touches.
        </>
      ),
      hold: { person: src.people[e]! },
    })
  if (t.stuck.length) {
    const by = new Map<number, number>()
    for (const s of t.stuck) by.set(s.status, (by.get(s.status) ?? 0) + 1)
    const [s0, n0] = [...by].sort((a, b) => b[1] - a[1])[0]!
    out.push({
      title: 'Stuck claims',
      body: (
        <>
          <b>{int(t.stuck.length)} claims</b> were touched 5+ times with no status change, most in{' '}
          <b>{src.statuses[s0] || '(blank)'}</b> ({int(n0)}).
        </>
      ),
      hold: { status: src.statuses[s0]! },
    })
  }
  const thin = t.people.filter(
    ([, v]) => v.touches >= THIN_NOTES.minTouches && v.blanks / v.touches >= THIN_NOTES.share,
  ).length
  if (t.kpi.dups || thin)
    out.push({
      title: 'Data hygiene',
      body: (
        <>
          {int(t.kpi.dups)} duplicate rows
          {thin > 0 && `; ${thin === 1 ? '1 person leaves' : `${int(thin)} people leave`} 10% or more of notes blank`}.
        </>
      ),
    })
  return out.length ? out : [{ title: 'Nothing yet', body: 'No touches in this selection.' }]
}
